import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import "./FinanceEntry.css";

const API_BASE_URL = (
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "http://localhost:5000"
    : process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in"
).replace(/\/$/, "");

const COMPANY_BANKS = {
  SVB: ["SVB-HDFC", "SVB-DBS", "SVB-KOTAK"],
};

const PAYMENT_MODES = ["NEFT", "Cash", "UPI", "Cheque"];

const defaultMasters = {
  officeUnits: [],
  assets: [],
  liabilities: [],
  customers: [],
  incomeHeads: [],
  subincomeHeads: [],
  incomeGroups: [],
  vendors: [],
  expenseHeads: [],
  subexpenseHeads: [],
  expenseGroups: [],
};

const todayDate = () => new Date().toISOString().slice(0, 10);

const getCurrentFinancialYearStart = () => {
  const now = new Date();
  const year = now.getFullYear();
  return now.getMonth() + 1 >= 4 ? year : year - 1;
};

const getFinancialYearLabel = (startYear) =>
  `${startYear}-${String(startYear + 1).slice(-2)}`;

const getFinancialYearForDate = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  return d.getMonth() + 1 >= 4 ? year : year - 1;
};

const getCalendarYearForFinancialMonth = (financialYearStart, month) =>
  Number(month) >= 4
    ? Number(financialYearStart)
    : Number(financialYearStart) + 1;

const getEmptyForm = () => ({
  date: todayDate(),
  amount: "",
  company: "SVB",
  bankAccount: "SVB-HDFC",
  paymentMode: "NEFT",
  gstPercent: "",
  tdsPercent: "",

  quoteSource: "svb",
  quoteDocumentId: "",
  quoteNo: "",
  quoteFile: null,
  quoteExistingFileName: "",

  proformaSource: "svb",
  proformaDocumentId: "",
  proformaNo: "",
  proformaFile: null,
  proformaExistingFileName: "",

  invoiceSource: "svb",
  invoiceDocumentId: "",
  invoiceNo: "",
  invoiceFile: null,
  invoiceExistingFileName: "",

  billNo: "",
  otherDocFile: null,
  otherDocExistingFileName: "",
  note: "",
});

export default function FinanceEntry({ user }) {
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [masters, setMasters] = useState(defaultMasters);
  const [popupOpen, setPopupOpen] = useState(false);
  const [entryType, setEntryType] = useState("income");
  const [form, setForm] = useState(getEmptyForm);
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("SVB");
  const [salesDocuments, setSalesDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);

  // Card status filters are independent for In Flow and Out Flow.
  const [incomeLockFilter, setIncomeLockFilter] = useState("all");
  const [expenseLockFilter, setExpenseLockFilter] = useState("all");

  // Top Head filters are independent for Income and Expense.
  const [incomeHeadFilter, setIncomeHeadFilter] = useState({
    field: "all",
    value: "",
  });

  const [expenseHeadFilter, setExpenseHeadFilter] = useState({
    field: "all",
    value: "",
  });

  const [dateFilter, setDateFilter] = useState({
    mode: "month",
    month: String(new Date().getMonth() + 1),
    quarter: String(
      new Date().getMonth() + 1 >= 4
        ? Math.floor((new Date().getMonth() + 1 - 4) / 3) + 1
        : 4,
    ),
    financialYear: String(getCurrentFinancialYearStart()),
    fromDate: "",
    toDate: "",
  });

  const role = String(user?.role || localStorage.getItem("role") || "")
    .trim()
    .toLowerCase();

  const canLock = ["admin", "head_accountant", "accountant"].includes(role);

  const getAuthConfig = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      Accept: "application/json",
    },
  });

  useEffect(() => {
    console.log("Finance API Base URL:", API_BASE_URL);
    fetchEntries();
    fetchProjects();
    fetchSalesDocuments();
    fetchMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildAmountValues = (source) => {
    const baseAmount = Number(source?.amount || 0);
    const gstPercent = Number(source?.gstPercent || 0);
    const tdsPercent = Number(source?.tdsPercent || 0);

    const gstAmount = (baseAmount * gstPercent) / 100;
    const tdsAmount = (baseAmount * tdsPercent) / 100;
    const totalAmount = baseAmount + gstAmount - tdsAmount;

    return {
      amount: baseAmount,
      gstPercent,
      gstAmount,
      tdsPercent,
      tdsAmount,
      totalAmount,
    };
  };

  const normalizeEntry = (entry) => {
    const company = entry.company || "SVB";
    const bankAccount =
      entry.bankAccount || COMPANY_BANKS[company]?.[0] || "SVB-HDFC";
    const paymentMode =
      entry.paymentMode && PAYMENT_MODES.includes(entry.paymentMode)
        ? entry.paymentMode
        : "NEFT";

    const amount = Number(entry.amount || 0);
    const gstPercent = Number(entry.gstPercent || 0);
    const tdsPercent = Number(entry.tdsPercent || 0);
    const gstAmount =
      entry.gstAmount !== undefined
        ? Number(entry.gstAmount || 0)
        : (amount * gstPercent) / 100;
    const tdsAmount =
      entry.tdsAmount !== undefined
        ? Number(entry.tdsAmount || 0)
        : (amount * tdsPercent) / 100;
    const totalAmount =
      entry.totalAmount !== undefined
        ? Number(entry.totalAmount || 0)
        : amount + gstAmount - tdsAmount;

    return {
      ...entry,
      company,
      bankAccount,
      paymentMode,
      amount,

      gstPercent,
      gstAmount,
      tdsPercent,
      tdsAmount,
      totalAmount,
      officeUnit: entry.officeUnit || "",
      subHead: entry.subHead || "",
      classificationType:
        entry.classificationType || (entry.classification ? "asset" : ""),
      classification: entry.classification || "",
      sourceType: entry.sourceType || "manual",
      paymentProofName: entry.paymentProofName || "",
      paymentProofUrl: entry.paymentProofUrl || "",
    };
  };

  // Payment Detail -> Finance Entry (read-only live view).
  // This does NOT write to FinanceEntry MongoDB. Existing linked records win.
  const fetchEntries = async () => {
    try {
      const auth = getAuthConfig();
      const [financeRes, incomeRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/finance-entries`, auth),
        axios.get(`${API_BASE_URL}/api/payments`, auth),
      ]);
      const financeRows = Array.isArray(financeRes.data)
        ? financeRes.data
        : financeRes.data?.data || financeRes.data?.entries || [];
      const incomeRows = Array.isArray(incomeRes.data) ? incomeRes.data : [];

      // Vendor payments endpoint is project-scoped in Payment Detail.
      const projectNames = Array.from(
        new Set(
          [
            ...projects.map((p) => p.name),
            ...incomeRows.map((p) => p.projectName),
            ...financeRows.map((p) => p.projectName),
          ].filter(Boolean),
        ),
      );
      const vendorResults = await Promise.allSettled(
        projectNames.map((name) =>
          axios.get(
            `${API_BASE_URL}/api/vendor-payments?projectName=${encodeURIComponent(name)}`,
            auth,
          ),
        ),
      );
      const vendorRows = vendorResults.flatMap((result, index) => {
        if (result.status !== "fulfilled") {
          console.warn(
            "Vendor payments not loaded for",
            projectNames[index],
            result.reason,
          );
          return [];
        }
        const rows = Array.isArray(result.value.data) ? result.value.data : [];
        return rows.map((row) => ({
          ...row,
          projectName: row.projectName || projectNames[index],
        }));
      });
      const existingIds = new Set(
        financeRows
          .map((row) => String(row.sourcePaymentId || ""))
          .filter(Boolean),
      );
      const seen = new Set(existingIds);
      const addVirtual = (source, type, prefix) => {
        const id = String(source?._id || "");
        if (!id) return null;
        const sourceKey = `${prefix}:${id}`;
        // Also recognize existing income records linked with raw Payment _id.
        if (seen.has(sourceKey) || (prefix === "payment" && seen.has(id)))
          return null;
        seen.add(sourceKey);
        const amount = Number(
          type === "income" ? source.amount : source.paymentAmt,
        );
        if (!Number.isFinite(amount) || amount <= 0) return null;
        const project = projects.find((p) => p.name === source.projectName);
        return normalizeEntry({
          _id: sourceKey,
          type,
          date:
            source.date ||
            source.paymentDate ||
            source.paidDate ||
            source.createdAt,
          amount,
          // Payment amount already represents actual cash received/paid.
          totalAmount: amount,
          gstPercent: Number(source.gstRate ?? source.gstPercent ?? 0),
          gstAmount: Number(source.gstAmount ?? 0),
          tdsPercent: Number(source.tdsPercent ?? 0),
          tdsAmount: Number(source.tdsAmount ?? 0),
          projectId: project?._id || source.projectId || null,
          projectName: source.projectName || "",
          partyName:
            type === "income"
              ? source.clientName || ""
              : source.vendorName || "",
          paymentMode: ["NEFT", "Cash", "UPI", "Cheque"].includes(source.method)
            ? source.method
            : "NEFT",
          invoiceNo: source.invoiceNo || "",
          paymentProofUrl:
            source.paymentProof || source.proof || source.screenshot || "",
          sourceType: "invoice_payment",
          sourcePaymentId: sourceKey,
          note: `Payment Detail ${type} (live view)`,
          company: "SVB",
          locked: false,
          status: "pending",
          isUnmapped: true,
          _paymentDetailLive: true,
        });
      };
      const liveRows = [
        ...incomeRows.map((row) => addVirtual(row, "income", "payment")),
        ...vendorRows.map((row) =>
          addVirtual(row, "expense", "vendor-payment"),
        ),
      ].filter(Boolean);
      setEntries([...financeRows.map(normalizeEntry), ...liveRows]);
    } catch (err) {
      console.error("Finance / Payment Detail fetch error:", err);
      // Do not silently present an empty ledger on a failed API request.
      alert(
        err.response?.data?.message ||
          "Finance / Payment Detail loading failed",
      );
    }
  };

  // Reload once project names are available so project-scoped expenses appear.
  useEffect(() => {
    if (projects.length) fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  const fetchMasters = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/finance-masters`,
        getAuthConfig(),
      );

      const data = res.data?.data || {};

      setMasters({
        officeUnits: Array.isArray(data.officeUnits) ? data.officeUnits : [],
        assets: Array.isArray(data.assets) ? data.assets : [],
        liabilities: Array.isArray(data.liabilities) ? data.liabilities : [],
        customers: Array.isArray(data.customers) ? data.customers : [],
        incomeHeads: Array.isArray(data.incomeHeads) ? data.incomeHeads : [],
        subincomeHeads: Array.isArray(data.subincomeHeads)
          ? data.subincomeHeads
          : [],
        incomeGroups: Array.isArray(data.incomeGroups) ? data.incomeGroups : [],
        vendors: Array.isArray(data.vendors) ? data.vendors : [],
        expenseHeads: Array.isArray(data.expenseHeads) ? data.expenseHeads : [],
        subexpenseHeads: Array.isArray(data.subexpenseHeads)
          ? data.subexpenseHeads
          : [],
        expenseGroups: Array.isArray(data.expenseGroups)
          ? data.expenseGroups
          : [],
      });
    } catch (err) {
      console.error("Finance masters fetch error:", {
        url: `${API_BASE_URL}/api/finance-masters`,
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setMasters({ ...defaultMasters });
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/projects`,
        getAuthConfig(),
      );
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.projects)
          ? res.data.projects
          : Array.isArray(res.data?.data)
            ? res.data.data
            : Array.isArray(res.data?.data?.projects)
              ? res.data.data.projects
              : Array.isArray(res.data?.result)
                ? res.data.result
                : Array.isArray(res.data?.results)
                  ? res.data.results
                  : Array.isArray(res.data?.projectData)
                    ? res.data.projectData
                    : [];

      const normalizedProjects = list
        .map((project) => ({
          ...project,
          _id:
            project?._id ||
            project?.id ||
            project?.projectId ||
            project?.project_id ||
            "",
          name:
            project?.name ||
            project?.projectName ||
            project?.project_name ||
            project?.title ||
            "",
        }))
        .filter((project) => project.name);

      setProjects(normalizedProjects);
      console.log("Finance projects loaded:", normalizedProjects.length);

      if (!normalizedProjects.length) {
        console.warn("No projects found in API response:", res.data);
      }
    } catch (err) {
      console.error("Projects fetch error:", {
        url: `${API_BASE_URL}/api/projects`,
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setProjects([]);
    }
  };

  const fetchSalesDocuments = async () => {
    try {
      setDocumentsLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/sales/documents`,
        getAuthConfig(),
      );

      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      setSalesDocuments(list);
    } catch (err) {
      console.error("Sales documents fetch error:", {
        url: `${API_BASE_URL}/api/sales/documents`,
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setSalesDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const getDocumentsByType = (type) =>
    salesDocuments.filter(
      (doc) =>
        String(doc?.type || "").toLowerCase() === type &&
        (!doc?.company || doc.company === form.company),
    );

  const getExistingAttachedFiles = (type) => {
    const fieldMap = {
      quotation: "quoteFileName",
      proforma: "proformaFileName",
      invoice: "invoiceFileName",
    };

    const field = fieldMap[type];
    if (!field) return [];

    const names = entries
      .filter((entry) => !entry?.company || entry.company === form.company)
      .map((entry) => String(entry?.[field] || "").trim())
      .filter(Boolean);

    return Array.from(new Set(names)).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  };

  const handleDocumentSelect = (type, selectedValue) => {
    const isAttachedValue = String(selectedValue || "").startsWith("file:");
    const existingFileName = isAttachedValue
      ? String(selectedValue).slice(5)
      : "";

    const doc = isAttachedValue
      ? null
      : salesDocuments.find((x) => String(x._id) === String(selectedValue));

    setForm((prev) => {
      const next = { ...prev };

      if (type === "quotation") {
        next.quoteFile = null;
        next.quoteExistingFileName = existingFileName;

        if (isAttachedValue) {
          next.quoteSource = "attach-existing";
          next.quoteDocumentId = "";
          next.quoteNo = "";
        } else {
          next.quoteSource = "svb";
          next.quoteDocumentId = selectedValue;
          next.quoteNo = doc?.number || "";
          next.quoteExistingFileName = "";
        }
      }

      if (type === "proforma") {
        next.proformaFile = null;
        next.proformaExistingFileName = existingFileName;

        if (isAttachedValue) {
          next.proformaSource = "attach-existing";
          next.proformaDocumentId = "";
          next.proformaNo = "";
        } else {
          next.proformaSource = "svb";
          next.proformaDocumentId = selectedValue;
          next.proformaNo = doc?.number || "";
          next.proformaExistingFileName = "";
        }
      }

      if (type === "invoice") {
        next.invoiceFile = null;
        next.invoiceExistingFileName = existingFileName;

        if (isAttachedValue) {
          next.invoiceSource = "attach-existing";
          next.invoiceDocumentId = "";
          next.invoiceNo = "";
        } else {
          next.invoiceSource = "svb";
          next.invoiceDocumentId = selectedValue;
          next.invoiceNo = doc?.number || "";
          next.invoiceExistingFileName = "";
        }
      }

      return next;
    });
  };

  const handleFileChange = (name, file) => {
    setForm((prev) => {
      const next = {
        ...prev,
        [name]: file || null,
      };

      if (name === "otherDocFile" && file) {
        next.otherDocExistingFileName = "";
      }

      if (name === "quoteFile" && file) {
        next.quoteSource = "attach";
        next.quoteDocumentId = "";
        next.quoteNo = "";
        next.quoteExistingFileName = "";
      }

      if (name === "proformaFile" && file) {
        next.proformaSource = "attach";
        next.proformaDocumentId = "";
        next.proformaNo = "";
        next.proformaExistingFileName = "";
      }

      if (name === "invoiceFile" && file) {
        next.invoiceSource = "attach";
        next.invoiceDocumentId = "";
        next.invoiceNo = "";
        next.invoiceExistingFileName = "";
      }

      return next;
    });
  };

  const addMaster = async (key) => {
    const value = prompt("Enter name");
    if (!value?.trim()) return;

    try {
      await axios.post(
        `${API_BASE_URL}/api/finance-masters/${key}`,
        { value: value.trim() },
        getAuthConfig(),
      );

      await fetchMasters();
    } catch (err) {
      console.error("Finance master add error:", err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Finance master add failed",
      );
    }
  };

  const openPopup = (type) => {
    setEditingEntryId(null);
    setEntryType(type);
    setForm({
      ...getEmptyForm(),
      company: "SVB",
      bankAccount: COMPANY_BANKS.SVB?.[0] || "SVB-HDFC",
    });
    setPopupOpen(true);
  };

  const openEditPopup = (entry) => {
    if (entry._paymentDetailLive) {
      alert("Edit this payment in Payment Detail.");
      return;
    }
    const current = normalizeEntry(entry);

    setEditingEntryId(current._id);
    setEntryType(current.type || "income");
    setForm({
      ...getEmptyForm(),
      date: current.date ? String(current.date).slice(0, 10) : todayDate(),
      amount: String(current.amount ?? ""),
      company: "SVB",
      bankAccount:
        current.paymentMode === "Cash" ? "" : current.bankAccount || "",
      paymentMode: current.paymentMode || "NEFT",
      gstPercent: current.gstPercent ? String(current.gstPercent) : "",
      tdsPercent: current.tdsPercent ? String(current.tdsPercent) : "",

      quoteSource: current.quoteSource || "svb",
      quoteDocumentId:
        current.quoteDocumentId?._id || current.quoteDocumentId || "",
      quoteNo: current.quoteNo || "",
      quoteFile: null,
      quoteExistingFileName: current.quoteFileName || "",

      proformaSource: current.proformaSource || "svb",
      proformaDocumentId:
        current.proformaDocumentId?._id || current.proformaDocumentId || "",
      proformaNo: current.proformaNo || "",
      proformaFile: null,
      proformaExistingFileName: current.proformaFileName || "",

      invoiceSource: current.invoiceSource || "svb",
      invoiceDocumentId:
        current.invoiceDocumentId?._id || current.invoiceDocumentId || "",
      invoiceNo: current.invoiceNo || "",
      invoiceFile: null,
      invoiceExistingFileName: current.invoiceFileName || "",

      billNo: current.billNo || "",
      otherDocFile: null,
      otherDocExistingFileName: current.otherDocFileName || "",
      note: current.note || "",
    });
    setPopupOpen(true);
  };

  const closePopup = () => {
    setPopupOpen(false);
    setEditingEntryId(null);
    setForm(getEmptyForm());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      if (name === "company") {
        return {
          ...prev,
          company: value,
          bankAccount:
            prev.paymentMode === "Cash" ? "" : COMPANY_BANKS[value]?.[0] || "",
          quoteDocumentId: "",
          quoteNo: "",
          quoteExistingFileName: "",
          proformaDocumentId: "",
          proformaNo: "",
          proformaExistingFileName: "",
          invoiceDocumentId: "",
          invoiceNo: "",
          invoiceExistingFileName: "",
        };
      }

      if (name === "paymentMode") {
        const isCash = value === "Cash";

        return {
          ...prev,
          paymentMode: value,
          bankAccount: isCash
            ? ""
            : prev.bankAccount || COMPANY_BANKS[prev.company]?.[0] || "",
          gstPercent: isCash ? "" : prev.gstPercent,
          tdsPercent: isCash ? "" : prev.tdsPercent,
        };
      }

      if (name === "quoteSource") {
        return {
          ...prev,
          quoteSource: value,
          quoteDocumentId: "",
          quoteNo: "",
          quoteFile: null,
        };
      }

      if (name === "proformaSource") {
        return {
          ...prev,
          proformaSource: value,
          proformaDocumentId: "",
          proformaNo: "",
          proformaFile: null,
        };
      }

      if (name === "invoiceSource") {
        return {
          ...prev,
          invoiceSource: value,
          invoiceDocumentId: "",
          invoiceNo: "",
          invoiceFile: null,
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const updateEntryWithDraggedData = async (entry, data) => {
    if (!entry?._id || !data?.value) return;

    if (entry._paymentDetailLive) return;
    if (entry.locked) {
      alert("Locked entry cannot be updated.");
      return;
    }

    const old = normalizeEntry(entry);

    const nextHead =
      data.type === "incomeHead" || data.type === "expenseHead"
        ? data.value
        : old.head || "";

    const nextSubHead =
      data.type === "subincomeHead" || data.type === "subexpenseHead"
        ? data.value
        : old.subHead || "";

    const nextGroup =
      data.type === "incomeGroup" || data.type === "expenseGroup"
        ? data.value
        : old.groupName || "";

    const nextProjectName =
      data.type === "project" ? data.value : old.projectName || "";

    const nextPartyName =
      data.type === "vendor" || data.type === "customer"
        ? data.value
        : old.partyName || "";

    const nextOfficeUnit =
      data.type === "officeUnit" ? data.value : old.officeUnit || "";

    const nextClassificationType =
      data.type === "asset"
        ? "asset"
        : data.type === "liability"
          ? "liability"
          : old.classificationType || "";

    const nextClassification =
      data.type === "asset" || data.type === "liability"
        ? data.value
        : old.classification || "";

    const payload = {
      type: old.type,
      date: old.date,
      amount: old.amount,
      gstPercent: old.gstPercent,
      gstAmount: old.gstAmount,
      tdsPercent: old.tdsPercent,
      tdsAmount: old.tdsAmount,
      totalAmount: old.totalAmount,
      company: old.company,
      bankAccount: old.bankAccount,
      paymentMode: old.paymentMode,

      projectId:
        data.type === "project"
          ? data.id
          : old.projectId?._id || old.projectId || null,

      projectName: nextProjectName,
      partyName: nextPartyName,
      officeUnit: nextOfficeUnit,
      head: nextHead,
      subHead: nextSubHead,
      groupName: nextGroup,
      classificationType: nextClassificationType,
      classification: nextClassification,

      isUnmapped:
        !nextHead ||
        !nextSubHead ||
        !nextGroup ||
        !nextProjectName ||
        !nextOfficeUnit,
      locked: false,
      status: "pending",
    };

    try {
      await axios.put(
        `${API_BASE_URL}/api/finance-entries/${old._id}`,
        payload,
        getAuthConfig(),
      );
      fetchEntries();
    } catch (err) {
      alert(err.response?.data?.message || "Entry update failed");
      console.error("Update entry error:", err);
    }
  };

  const saveEntry = async () => {
    if (!form.date || !form.amount) {
      alert("Date and Amount required");
      return;
    }

    const amountValues = buildAmountValues(form);
    const existingEntry = editingEntryId
      ? entries.find((x) => x._id === editingEntryId)
      : null;

    const payload = {
      type: entryType,
      date: form.date,
      ...amountValues,
      company: form.company,
      bankAccount: form.paymentMode === "Cash" ? "" : form.bankAccount,
      paymentMode: form.paymentMode,

      quoteNo: form.quoteNo,
      proformaNo: form.proformaNo,
      invoiceNo: form.invoiceNo,
      billNo: form.billNo,
      note: form.note,

      quoteSource: form.quoteSource,
      quoteDocumentId: form.quoteDocumentId || "",
      quoteFileName: form.quoteFile?.name || form.quoteExistingFileName || "",
      quoteFileKey: existingEntry?.quoteFileKey || "",

      proformaSource: form.proformaSource,
      proformaDocumentId: form.proformaDocumentId || "",
      proformaFileName:
        form.proformaFile?.name || form.proformaExistingFileName || "",
      proformaFileKey: existingEntry?.proformaFileKey || "",

      invoiceSource: form.invoiceSource,
      invoiceDocumentId: form.invoiceDocumentId || "",
      invoiceFileName:
        form.invoiceFile?.name || form.invoiceExistingFileName || "",
      invoiceFileKey: existingEntry?.invoiceFileKey || "",

      otherDocFileName:
        form.otherDocFile?.name || form.otherDocExistingFileName || "",
      otherDocFileKey: existingEntry?.otherDocFileKey || "",

      projectId:
        existingEntry?.projectId?._id || existingEntry?.projectId || "",
      projectName: existingEntry?.projectName || "",
      partyName: existingEntry?.partyName || "",
      officeUnit: existingEntry?.officeUnit || "",
      head: existingEntry?.head || "",
      subHead: existingEntry?.subHead || "",
      groupName: existingEntry?.groupName || "",
      classificationType: existingEntry?.classificationType || "",
      classification: existingEntry?.classification || "",

      isUnmapped: existingEntry ? Boolean(existingEntry.isUnmapped) : true,
    };

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        formData.append(key, "");
      } else {
        formData.append(key, String(value));
      }
    });

    if (form.quoteFile) {
      formData.append("quoteFile", form.quoteFile);
    }

    if (form.proformaFile) {
      formData.append("proformaFile", form.proformaFile);
    }

    if (form.invoiceFile) {
      formData.append("invoiceFile", form.invoiceFile);
    }

    if (form.otherDocFile) {
      formData.append("otherDocFile", form.otherDocFile);
    }

    try {
      setLoading(true);

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Accept: "application/json",
        },
      };

      if (editingEntryId) {
        await axios.put(
          `${API_BASE_URL}/api/finance-entries/${editingEntryId}`,
          formData,
          config,
        );
        alert("Entry updated successfully");
      } else {
        await axios.post(
          `${API_BASE_URL}/api/finance-entries`,
          formData,
          config,
        );
        alert("Entry saved for review");
      }

      closePopup();
      fetchEntries();
    } catch (err) {
      console.error("SAVE ERROR FULL:", err.response?.data);

      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Entry save failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const openPaymentProof = async (proofUrl) => {
    const value = String(proofUrl || "").trim();

    if (!value) {
      alert("Payment proof is not available.");
      return;
    }

    /*
      Old records were stored as:
      /api/download?key=Finance%2FSales%2FPaymentProofs%2Ffile.png

      The current server exposes D:\\SVB-upload through:
      /api/files

      Convert old URLs automatically so existing records also work.
    */
    const normalizePaymentProofPath = (rawValue) => {
      const raw = String(rawValue || "").trim();

      if (!raw) return "";

      if (/^https?:\/\//i.test(raw)) {
        return raw;
      }

      if (raw.startsWith("/api/download")) {
        try {
          const tempUrl = new URL(raw, window.location.origin);
          const key = tempUrl.searchParams.get("key") || "";

          if (key) {
            const cleanKey = decodeURIComponent(key)
              .replace(/\\/g, "/")
              .replace(/^\/+/, "");

            return `/api/files/${cleanKey
              .split("/")
              .map((part) => encodeURIComponent(part))
              .join("/")}`;
          }
        } catch (error) {
          console.error("Payment proof legacy URL parse error:", error);
        }
      }

      if (raw.startsWith("/api/files/")) {
        return raw;
      }

      /*
        If DB contains only a storage key such as:
        Finance/Sales/PaymentProofs/file.png
      */
      const cleanRaw = raw.replace(/\\/g, "/").replace(/^\/+/, "");

      return `/api/files/${cleanRaw
        .split("/")
        .map((part) => encodeURIComponent(part))
        .join("/")}`;
    };

    const normalizedPath = normalizePaymentProofPath(value);

    const finalUrl = /^https?:\/\//i.test(normalizedPath)
      ? normalizedPath
      : `${API_BASE_URL}${
          normalizedPath.startsWith("/") ? "" : "/"
        }${normalizedPath}`;

    /*
      Open immediately so the browser does not block the tab.
    */
    const proofWindow = window.open("", "_blank");

    if (!proofWindow) {
      alert("Please allow pop-ups for this site to open payment proof.");
      return;
    }

    proofWindow.document.title = "Payment Proof";
    proofWindow.document.body.innerHTML =
      '<div style="font-family:Arial;padding:24px">Loading payment proof...</div>';

    try {
      const response = await axios.get(finalUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Accept: "*/*",
        },
        responseType: "blob",
      });

      const contentType =
        response.headers?.["content-type"] ||
        response.data?.type ||
        "application/octet-stream";

      if (
        contentType.includes("application/json") ||
        contentType.includes("text/html")
      ) {
        const errorText = await response.data.text();

        let message = "Payment proof could not be opened.";

        try {
          const parsed = JSON.parse(errorText);
          message = parsed?.message || parsed?.error || message;
        } catch (_) {
          if (errorText) message = errorText;
        }

        throw new Error(message);
      }

      const proofBlob = new Blob([response.data], {
        type: contentType,
      });

      const objectUrl = URL.createObjectURL(proofBlob);
      proofWindow.location.href = objectUrl;

      setTimeout(
        () => {
          URL.revokeObjectURL(objectUrl);
        },
        5 * 60 * 1000,
      );
    } catch (err) {
      console.error("Open payment proof error:", {
        originalProofUrl: value,
        normalizedPath,
        finalUrl,
        status: err.response?.status,
        message: err.message,
      });

      let message = err?.message || "Payment proof could not be opened.";

      if (err.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();

          try {
            const parsed = JSON.parse(errorText);
            message = parsed?.message || parsed?.error || message;
          } catch (_) {
            if (errorText) message = errorText;
          }
        } catch (_) {
          // Keep existing message.
        }
      } else {
        message =
          err.response?.data?.message || err.response?.data?.error || message;
      }

      proofWindow.document.body.innerHTML = `
        <div style="font-family:Arial;padding:24px;color:#b91c1c">
          <strong>Payment proof could not be opened.</strong>
          <div style="margin-top:8px;color:#334155">${String(message)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")}</div>
          <div style="margin-top:12px;font-size:12px;color:#64748b">
            ${String(normalizedPath)
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")}
          </div>
        </div>
      `;
    }
  };

  const openSalesDocumentPdf = async (documentId) => {
    const id =
      typeof documentId === "object"
        ? documentId?._id || documentId?.id || ""
        : documentId || "";

    if (!id) {
      alert("Sales document ID not found.");
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/sales/documents/${id}/pdf`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob",
        },
      );

      const contentType =
        response.headers?.["content-type"] || "application/pdf";

      const pdfBlob = new Blob([response.data], {
        type: contentType,
      });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const newWindow = window.open(pdfUrl, "_blank", "noopener,noreferrer");

      if (!newWindow) {
        alert("Please allow pop-ups to open the document.");
      }

      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 60000);
    } catch (err) {
      console.error("Open Sales PDF Error:", err);

      let message = "Document PDF open nahi ho paya.";

      if (err.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const parsed = JSON.parse(errorText);
          message = parsed?.message || parsed?.error || message;
        } catch (_) {
          // Keep fallback message
        }
      } else {
        message =
          err.response?.data?.message || err.response?.data?.error || message;
      }

      if (err.response?.status === 401) {
        message = "Login session expired. Please login again.";
      }

      if (err.response?.status === 404) {
        message =
          message ||
          "PDF abhi server par save nahi hai. Sales document ka PDF generate/save karna hoga.";
      }

      alert(message);
    }
  };

  const lockEntry = async (id) => {
    if (!window.confirm("Lock this entry?")) return;

    try {
      await axios.patch(
        `${API_BASE_URL}/api/finance-entries/${id}/lock`,
        {},
        getAuthConfig(),
      );
      fetchEntries();
    } catch (err) {
      alert(err.response?.data?.message || "Entry lock failed");
      console.error("Lock entry error:", err);
    }
  };

  const availableFinancialYears = useMemo(() => {
    const years = new Set();

    entries.forEach((entry) => {
      if (!entry?.date) return;
      const fy = getFinancialYearForDate(entry.date);
      if (fy !== null) years.add(fy);
    });

    years.add(getCurrentFinancialYearStart());

    return Array.from(years).sort((a, b) => b - a);
  }, [entries]);

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      if (!entry?.date) return false;
      if ((entry.company || "SVB") !== selectedCompany) return false;

      const d = new Date(entry.date);
      if (Number.isNaN(d.getTime())) return false;

      const financialYearStart = Number(
        dateFilter.financialYear || getCurrentFinancialYearStart(),
      );
      const month = Number(dateFilter.month || 4);
      const quarter = Number(dateFilter.quarter || 1);

      if (dateFilter.mode === "month") {
        const calendarYear = getCalendarYearForFinancialMonth(
          financialYearStart,
          month,
        );

        return d.getFullYear() === calendarYear && d.getMonth() + 1 === month;
      }

      if (dateFilter.mode === "quarter") {
        const quarterRanges = {
          1: {
            from: new Date(financialYearStart, 3, 1),
            to: new Date(financialYearStart, 5, 30, 23, 59, 59, 999),
          },
          2: {
            from: new Date(financialYearStart, 6, 1),
            to: new Date(financialYearStart, 8, 30, 23, 59, 59, 999),
          },
          3: {
            from: new Date(financialYearStart, 9, 1),
            to: new Date(financialYearStart, 11, 31, 23, 59, 59, 999),
          },
          4: {
            from: new Date(financialYearStart + 1, 0, 1),
            to: new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999),
          },
        };

        const range = quarterRanges[quarter] || quarterRanges[1];
        return d >= range.from && d <= range.to;
      }

      if (dateFilter.mode === "year") {
        const from = new Date(financialYearStart, 3, 1);
        const to = new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999);

        return d >= from && d <= to;
      }

      if (dateFilter.mode === "custom") {
        const from = dateFilter.fromDate
          ? new Date(`${dateFilter.fromDate}T00:00:00`)
          : null;
        const to = dateFilter.toDate
          ? new Date(`${dateFilter.toDate}T23:59:59.999`)
          : null;

        if (from && d < from) return false;
        if (to && d > to) return false;

        return true;
      }

      return true;
    });
  }, [entries, dateFilter, selectedCompany]);

  const clearDateFilter = () => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;

    setDateFilter({
      mode: "month",
      month: String(currentMonth),
      quarter: String(
        currentMonth >= 4 ? Math.floor((currentMonth - 4) / 3) + 1 : 4,
      ),
      financialYear: String(getCurrentFinancialYearStart()),
      fromDate: "",
      toDate: "",
    });
  };

  const sortUnlockedFirst = (list) =>
    [...list].sort((a, b) => {
      // Keep unlocked entries at the top and locked entries at the bottom.
      if (Boolean(a.locked) !== Boolean(b.locked)) {
        return a.locked ? 1 : -1;
      }

      // Within the same lock status, show the latest entry first.
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();

      if (!Number.isNaN(dateA) && !Number.isNaN(dateB) && dateA !== dateB) {
        return dateB - dateA;
      }

      // Fallback to createdAt when dates are the same or unavailable.
      const createdAtA = new Date(a.createdAt || 0).getTime();
      const createdAtB = new Date(b.createdAt || 0).getTime();
      return createdAtB - createdAtA;
    });

  // Base entries for the selected company/date period.
  const periodIncomeEntries = sortUnlockedFirst(
    filteredEntries.filter((e) => e.type === "income"),
  );

  const periodExpenseEntries = sortUnlockedFirst(
    filteredEntries.filter((e) => e.type === "expense"),
  );

  /*
   * Top Head filter mirrors the actual left/right master panels.
   *
   * Income:
   * Project Select, Office Unit, Income Parent Head, Income Sub Head,
   * Income Group, Asset, Liability
   *
   * Expense:
   * Project Select, Office Unit, Vendor Name, Expense Parent Head,
   * Expense Sub Head, Expense Group, Asset, Liability
   */
  const getHeadFilterOptions = (type, field) => {
    if (!field || field === "all") return [];

    const source =
      type === "income" ? periodIncomeEntries : periodExpenseEntries;

    let masterValues = [];
    let entryValues = [];

    if (field === "project") {
      masterValues = projects.map((project) => project?.name);
      entryValues = source.map((entry) => entry?.projectName);
    }

    if (field === "officeUnit") {
      masterValues = masters.officeUnits;
      entryValues = source.map((entry) => entry?.officeUnit);
    }

    if (field === "customer") {
      masterValues = masters.customers;
      entryValues = source.map((entry) => entry?.partyName);
    }

    if (field === "vendor") {
      masterValues = masters.vendors;
      entryValues = source.map((entry) => entry?.partyName);
    }

    if (field === "parentHead") {
      masterValues =
        type === "income" ? masters.incomeHeads : masters.expenseHeads;
      entryValues = source.map((entry) => entry?.head);
    }

    if (field === "subHead") {
      masterValues =
        type === "income" ? masters.subincomeHeads : masters.subexpenseHeads;
      entryValues = source.map((entry) => entry?.subHead);
    }

    if (field === "group") {
      masterValues =
        type === "income" ? masters.incomeGroups : masters.expenseGroups;
      entryValues = source.map((entry) => entry?.groupName);
    }

    if (field === "asset") {
      masterValues = masters.assets;
      entryValues = source
        .filter((entry) => entry?.classificationType === "asset")
        .map((entry) => entry?.classification);
    }

    if (field === "liability") {
      masterValues = masters.liabilities;
      entryValues = source
        .filter((entry) => entry?.classificationType === "liability")
        .map((entry) => entry?.classification);
    }

    return Array.from(
      new Set(
        [...(Array.isArray(masterValues) ? masterValues : []), ...entryValues]
          .map((value) => String(value || "").trim())
          .filter(Boolean),
      ),
    ).sort((a, b) =>
      a.localeCompare(b, undefined, {
        sensitivity: "base",
      }),
    );
  };

  const incomeHeadOptions = getHeadFilterOptions(
    "income",
    incomeHeadFilter.field,
  );

  const expenseHeadOptions = getHeadFilterOptions(
    "expense",
    expenseHeadFilter.field,
  );

  const filterByHead = (list, filter) => {
    if (!filter?.field || filter.field === "all" || !filter.value) {
      return list;
    }

    const selectedValue = String(filter.value || "").trim();

    return list.filter((entry) => {
      if (filter.field === "project") {
        return String(entry?.projectName || "").trim() === selectedValue;
      }

      if (filter.field === "officeUnit") {
        return String(entry?.officeUnit || "").trim() === selectedValue;
      }

      if (filter.field === "customer" || filter.field === "vendor") {
        return String(entry?.partyName || "").trim() === selectedValue;
      }

      if (filter.field === "parentHead") {
        return String(entry?.head || "").trim() === selectedValue;
      }

      if (filter.field === "subHead") {
        return String(entry?.subHead || "").trim() === selectedValue;
      }

      if (filter.field === "group") {
        return String(entry?.groupName || "").trim() === selectedValue;
      }

      if (filter.field === "asset") {
        return (
          entry?.classificationType === "asset" &&
          String(entry?.classification || "").trim() === selectedValue
        );
      }

      if (filter.field === "liability") {
        return (
          entry?.classificationType === "liability" &&
          String(entry?.classification || "").trim() === selectedValue
        );
      }

      return true;
    });
  };

  // These lists drive the cards and summary values.
  const allIncomeEntries = filterByHead(periodIncomeEntries, incomeHeadFilter);

  const allExpenseEntries = filterByHead(
    periodExpenseEntries,
    expenseHeadFilter,
  );

  const filterByLockStatus = (list, filter) => {
    if (filter === "locked") {
      return list.filter((entry) => Boolean(entry.locked));
    }

    if (filter === "unlocked") {
      return list.filter((entry) => !Boolean(entry.locked));
    }

    return list;
  };

  // Visible cards according to Head + Locked/Unlocked filters.
  const incomeEntries = filterByLockStatus(allIncomeEntries, incomeLockFilter);

  const expenseEntries = filterByLockStatus(
    allExpenseEntries,
    expenseLockFilter,
  );

  const clearHeadFilters = () => {
    setIncomeHeadFilter({
      field: "all",
      value: "",
    });

    setExpenseHeadFilter({
      field: "all",
      value: "",
    });
  };

  // Amount displayed beside each Locked / Unlocked / All filter.
  // Uses totalAmount when available, otherwise falls back to amount.
  const visibleIncomeAmount = incomeEntries.reduce(
    (sum, entry) => sum + Number(entry.totalAmount ?? entry.amount ?? 0),
    0,
  );

  const visibleExpenseAmount = expenseEntries.reduce(
    (sum, entry) => sum + Number(entry.totalAmount ?? entry.amount ?? 0),
    0,
  );

  const previewAmount = buildAmountValues(form);

  const totalInflow = allIncomeEntries.reduce(
    (sum, e) => sum + Number(e.totalAmount || e.amount || 0),
    0,
  );
  const totalOutflow = allExpenseEntries.reduce(
    (sum, e) => sum + Number(e.totalAmount || e.amount || 0),
    0,
  );
  const cashFlow = totalInflow - totalOutflow;

  const pnlIncome = allIncomeEntries
    .filter((e) => !["asset", "liability"].includes(e.classificationType))
    .reduce((sum, e) => sum + Number(e.totalAmount || e.amount || 0), 0);

  const pnlExpense = allExpenseEntries
    .filter((e) => !["asset", "liability"].includes(e.classificationType))
    .reduce((sum, e) => sum + Number(e.totalAmount || e.amount || 0), 0);

  const profitLoss = pnlIncome - pnlExpense;

  const maxInflowEntry = allIncomeEntries.reduce(
    (max, e) =>
      Number(e.totalAmount || e.amount || 0) >
      Number(max?.totalAmount || max?.amount || 0)
        ? e
        : max,
    null,
  );

  const maxOutflowEntry = allExpenseEntries.reduce(
    (max, e) =>
      Number(e.totalAmount || e.amount || 0) >
      Number(max?.totalAmount || max?.amount || 0)
        ? e
        : max,
    null,
  );

  const formatMoney = (value) =>
    `₹ ${Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;

  return (
    <div className="finance-page">
      <div className="finance-top">
        <div>
          <button
            type="button"
            className="finance-back-button"
            onClick={() => window.history.back()}
            title="Back to previous page"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "7px",
              marginBottom: "8px",
              padding: "7px 12px",
              border: "1px solid #d7dee8",
              borderRadius: "7px",
              background: "#ffffff",
              color: "#334155",
              fontSize: "13px",
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
            }}
          >
            <span
              aria-hidden="true"
              style={{ fontSize: "17px", lineHeight: 1 }}
            >
              ←
            </span>
            Back
          </button>

          <p className="finance-eyebrow">Finance Control Panel</p>
          <h2>Income & Expense Entry</h2>
        </div>

        <div className="finance-heading-company">
          <span>Company</span>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            disabled
          >
            <option value="SVB">SVB</option>
          </select>
        </div>
      </div>

      <section className="finance-summary-grid">
        <div className="finance-summary-card">
          <span>Cash Flow</span>
          <strong>{formatMoney(cashFlow)}</strong>
          <small>Total Inflow - Total Outflow</small>
        </div>

        <div className="finance-summary-card">
          <span>P &amp; L</span>
          <strong>{formatMoney(profitLoss)}</strong>
          <small>Operating Income - Expense (Asset/Liability excluded)</small>
        </div>

        <div className="finance-summary-card">
          <span>Max Inflow</span>
          <strong>
            {formatMoney(maxInflowEntry?.totalAmount || maxInflowEntry?.amount)}
          </strong>
          <small>{maxInflowEntry?.projectName || "Not mapped"}</small>
        </div>

        <div className="finance-summary-card">
          <span>Max Outflow</span>
          <strong>
            {formatMoney(
              maxOutflowEntry?.totalAmount || maxOutflowEntry?.amount,
            )}
          </strong>
          <small>{maxOutflowEntry?.projectName || "Not mapped"}</small>
        </div>
      </section>

      <div className="finance-top-filter-grid">
        <section className="finance-filter-card">
          <div className="finance-filter-header">
            <div>
              <h3>Financial Period</h3>
              <p>
                View finance data by Indian Financial Year (April to March),
                month, quarter or custom date range.
              </p>
            </div>

            <button
              type="button"
              className="finance-filter-clear"
              onClick={clearDateFilter}
            >
              Reset
            </button>
          </div>

          <div className="finance-filter-row">
            <label>
              Filter By
              <select
                value={dateFilter.mode}
                onChange={(e) =>
                  setDateFilter((prev) => ({ ...prev, mode: e.target.value }))
                }
              >
                <option value="month">Month Wise</option>
                <option value="quarter">Quarterly</option>
                <option value="year">Year Wise</option>
                <option value="custom">Custom Range</option>
              </select>
            </label>

            {(dateFilter.mode === "month" ||
              dateFilter.mode === "quarter" ||
              dateFilter.mode === "year") && (
              <label>
                Financial Year
                <select
                  value={dateFilter.financialYear}
                  onChange={(e) =>
                    setDateFilter((prev) => ({
                      ...prev,
                      financialYear: e.target.value,
                    }))
                  }
                >
                  {availableFinancialYears.map((year) => (
                    <option key={year} value={year}>
                      {getFinancialYearLabel(year)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {dateFilter.mode === "month" && (
              <label>
                Month
                <select
                  value={dateFilter.month}
                  onChange={(e) =>
                    setDateFilter((prev) => ({
                      ...prev,
                      month: e.target.value,
                    }))
                  }
                >
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </label>
            )}

            {dateFilter.mode === "quarter" && (
              <label>
                Quarter
                <select
                  value={dateFilter.quarter}
                  onChange={(e) =>
                    setDateFilter((prev) => ({
                      ...prev,
                      quarter: e.target.value,
                    }))
                  }
                >
                  <option value="1">Q1 (Apr - Jun)</option>
                  <option value="2">Q2 (Jul - Sep)</option>
                  <option value="3">Q3 (Oct - Dec)</option>
                  <option value="4">Q4 (Jan - Mar)</option>
                </select>
              </label>
            )}

            {dateFilter.mode === "custom" && (
              <>
                <label>
                  From Date
                  <input
                    type="date"
                    value={dateFilter.fromDate}
                    onChange={(e) =>
                      setDateFilter((prev) => ({
                        ...prev,
                        fromDate: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  To Date
                  <input
                    type="date"
                    min={dateFilter.fromDate || undefined}
                    value={dateFilter.toDate}
                    onChange={(e) =>
                      setDateFilter((prev) => ({
                        ...prev,
                        toDate: e.target.value,
                      }))
                    }
                  />
                </label>
              </>
            )}

            <div className="finance-filter-count">
              <span>Showing</span>
              <b>{filteredEntries.length}</b>
              <small>of {entries.length} entries</small>
            </div>
          </div>
        </section>

        <section className="finance-head-filter-card">
          <div className="finance-filter-header finance-head-filter-header">
            <div>
              <h3>Heads</h3>
            </div>

            <button
              type="button"
              className="finance-filter-clear"
              onClick={clearHeadFilters}
            >
              Reset
            </button>
          </div>

          <div className="finance-head-filter-panels">
            <div className="finance-head-filter-panel income-head-filter-panel">
              <div className="finance-head-filter-title">Income</div>

              <div className="finance-head-filter-fields">
                <label>
                  Head
                  <select
                    value={incomeHeadFilter.field}
                    onChange={(e) =>
                      setIncomeHeadFilter({
                        field: e.target.value,
                        value: "",
                      })
                    }
                  >
                    <option value="all">All</option>
                    <option value="project">Project Select</option>
                    <option value="officeUnit">Office Unit</option>
                    <option value="customer">Customer Name</option>
                    <option value="parentHead">Income Parent Head</option>
                    <option value="subHead">Income Sub Head</option>
                    <option value="group">Income Group</option>
                    <option value="asset">Asset</option>
                    <option value="liability">Liability</option>
                  </select>
                </label>

                <label>
                  Value
                  <select
                    value={incomeHeadFilter.value}
                    disabled={incomeHeadFilter.field === "all"}
                    onChange={(e) =>
                      setIncomeHeadFilter((prev) => ({
                        ...prev,
                        value: e.target.value,
                      }))
                    }
                  >
                    <option value="">
                      {incomeHeadFilter.field === "all"
                        ? "Select Value"
                        : "All Values"}
                    </option>

                    {incomeHeadOptions.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="finance-head-filter-panel expense-head-filter-panel">
              <div className="finance-head-filter-title">Expense</div>

              <div className="finance-head-filter-fields">
                <label>
                  Head
                  <select
                    value={expenseHeadFilter.field}
                    onChange={(e) =>
                      setExpenseHeadFilter({
                        field: e.target.value,
                        value: "",
                      })
                    }
                  >
                    <option value="all">All</option>
                    <option value="project">Project Select</option>
                    <option value="officeUnit">Office Unit</option>
                    <option value="vendor">Vendor Name</option>
                    <option value="parentHead">Expense Parent Head</option>
                    <option value="subHead">Expense Sub Head</option>
                    <option value="group">Expense Group</option>
                    <option value="asset">Asset</option>
                    <option value="liability">Liability</option>
                  </select>
                </label>

                <label>
                  Value
                  <select
                    value={expenseHeadFilter.value}
                    disabled={expenseHeadFilter.field === "all"}
                    onChange={(e) =>
                      setExpenseHeadFilter((prev) => ({
                        ...prev,
                        value: e.target.value,
                      }))
                    }
                  >
                    <option value="">
                      {expenseHeadFilter.field === "all"
                        ? "Select Value"
                        : "All Values"}
                    </option>

                    {expenseHeadOptions.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="finance-main-grid">
        <aside className="finance-side-panel finance-master-panel income-master-panel">
          <SelectDragBox
            title="Project Select"
            color="green"
            items={projects.map((p) => ({
              label: p.name,
              value: p._id || p.id || p.projectId || p.name,
              raw: p,
            }))}
            emptyText="No projects found"
            getData={(p) => ({
              type: "project",
              id: p._id || p.id || p.projectId || null,
              value: p.name,
            })}
          />
          <SelectDragBox
            title="Office Unit"
            color="green"
            add={() => addMaster("officeUnits")}
            items={masters.officeUnits.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No office unit added"
            getData={(x) => ({ type: "officeUnit", value: x })}
          />

          <SelectDragBox
            title="Income Parent Head"
            color="green"
            add={() => addMaster("incomeHeads")}
            items={masters.incomeHeads.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No income head added"
            getData={(x) => ({ type: "incomeHead", value: x })}
          />
          <SelectDragBox
            title="Income Sub Head"
            color="green"
            add={() => addMaster("subincomeHeads")}
            items={masters.subincomeHeads.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No sub income head added"
            getData={(x) => ({ type: "subincomeHead", value: x })}
          />

          <SelectDragBox
            title="Income Group"
            color="green"
            add={() => addMaster("incomeGroups")}
            items={masters.incomeGroups.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No income group added"
            getData={(x) => ({ type: "incomeGroup", value: x })}
          />

          <SelectDragBox
            title="Asset"
            color="green"
            add={() => addMaster("assets")}
            items={masters.assets.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No asset added"
            getData={(x) => ({ type: "asset", value: x })}
          />

          <SelectDragBox
            title="Liability"
            color="green"
            add={() => addMaster("liabilities")}
            items={masters.liabilities.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No liability added"
            getData={(x) => ({ type: "liability", value: x })}
          />
        </aside>

        <main className="finance-entries-area">
          <section className="finance-column income-column">
            <div className="finance-column-header">
              <div>
                <h3>In Flow</h3>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <select
                  value={incomeLockFilter}
                  onChange={(e) => setIncomeLockFilter(e.target.value)}
                  title="Filter In Flow entries by lock status"
                  style={{
                    height: "36px",
                    minWidth: "112px",
                    padding: "0 30px 0 11px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    background: "#ffffff",
                    color: "#334155",
                    fontSize: "12px",
                    fontWeight: 700,
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value="all">All</option>
                  <option value="unlocked">Unlocked</option>
                  <option value="locked">Locked</option>
                </select>

                <div
                  title={`${incomeEntries.length} visible In Flow entr${
                    incomeEntries.length === 1 ? "y" : "ies"
                  }`}
                  style={{
                    height: "36px",
                    minWidth: "112px",
                    padding: "0 12px",
                    border: "1px solid #bbf7d0",
                    borderRadius: "8px",
                    background: "#f0fdf4",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span
                    style={{
                      color: "#64748b",
                      fontSize: "10px",
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {incomeEntries.length}
                  </span>
                  <strong
                    style={{
                      color: "#166534",
                      fontSize: "13px",
                      fontWeight: 800,
                    }}
                  >
                    {formatMoney(visibleIncomeAmount)}
                  </strong>
                </div>

                <button
                  className="finance-btn income-btn"
                  onClick={() => openPopup("income")}
                >
                  + Add In Flow
                </button>
              </div>
            </div>

            {incomeEntries.length === 0 && (
              <div className="finance-empty-box">No income entries found</div>
            )}

            {incomeEntries.map((e) => (
              <EntryCard
                key={e._id}
                entry={normalizeEntry(e)}
                type="income"
                canLock={canLock}
                onLock={lockEntry}
                onEdit={openEditPopup}
                onDropToCard={updateEntryWithDraggedData}
                onOpenSalesDocument={openSalesDocumentPdf}
                onOpenPaymentProof={openPaymentProof}
              />
            ))}
          </section>

          <section className="finance-column expense-column">
            <div className="finance-column-header">
              <div>
                <h3>Out Flow</h3>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <select
                  value={expenseLockFilter}
                  onChange={(e) => setExpenseLockFilter(e.target.value)}
                  title="Filter Out Flow entries by lock status"
                  style={{
                    height: "36px",
                    minWidth: "112px",
                    padding: "0 30px 0 11px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    background: "#ffffff",
                    color: "#334155",
                    fontSize: "12px",
                    fontWeight: 700,
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value="all">All</option>
                  <option value="unlocked">Unlocked</option>
                  <option value="locked">Locked</option>
                </select>

                <div
                  title={`${expenseEntries.length} visible Out Flow entr${
                    expenseEntries.length === 1 ? "y" : "ies"
                  }`}
                  style={{
                    height: "36px",
                    minWidth: "112px",
                    padding: "0 12px",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    background: "#fef2f2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span
                    style={{
                      color: "#64748b",
                      fontSize: "10px",
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {expenseEntries.length}
                  </span>
                  <strong
                    style={{
                      color: "#b91c1c",
                      fontSize: "13px",
                      fontWeight: 800,
                    }}
                  >
                    {formatMoney(visibleExpenseAmount)}
                  </strong>
                </div>

                <button
                  className="finance-btn expense-btn"
                  onClick={() => openPopup("expense")}
                >
                  + Add Out Flow
                </button>
              </div>
            </div>

            {expenseEntries.length === 0 && (
              <div className="finance-empty-box">No expense entries found</div>
            )}

            {expenseEntries.map((e) => (
              <EntryCard
                key={e._id}
                entry={normalizeEntry(e)}
                type="expense"
                canLock={canLock}
                onLock={lockEntry}
                onEdit={openEditPopup}
                onDropToCard={updateEntryWithDraggedData}
                onOpenSalesDocument={openSalesDocumentPdf}
                onOpenPaymentProof={openPaymentProof}
              />
            ))}
          </section>
        </main>

        <aside className="finance-side-panel finance-master-panel expense-master-panel">
          <SelectDragBox
            title="Project Select"
            color="red"
            items={projects.map((p) => ({
              label: p.name,
              value: p._id || p.id || p.projectId || p.name,
              raw: p,
            }))}
            emptyText="No projects found"
            getData={(p) => ({
              type: "project",
              id: p._id || p.id || p.projectId || null,
              value: p.name,
            })}
          />
          <SelectDragBox
            title="Office Unit"
            color="red"
            add={() => addMaster("officeUnits")}
            items={masters.officeUnits.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No office unit added"
            getData={(x) => ({ type: "officeUnit", value: x })}
          />

          <SelectDragBox
            title="Vendor Name"
            color="red"
            add={() => addMaster("vendors")}
            items={masters.vendors.map((x) => ({ label: x, value: x, raw: x }))}
            emptyText="No vendor added"
            getData={(x) => ({ type: "vendor", value: x })}
          />

          <SelectDragBox
            title="Expense Parent Head"
            color="red"
            add={() => addMaster("expenseHeads")}
            items={masters.expenseHeads.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No expense head added"
            getData={(x) => ({ type: "expenseHead", value: x })}
          />

          <SelectDragBox
            title="Expense Sub Head"
            color="red"
            add={() => addMaster("subexpenseHeads")}
            items={masters.subexpenseHeads.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No expense sub head added"
            getData={(x) => ({ type: "subexpenseHead", value: x })}
          />

          <SelectDragBox
            title="Expense Group"
            color="red"
            add={() => addMaster("expenseGroups")}
            items={masters.expenseGroups.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No expense group added"
            getData={(x) => ({ type: "expenseGroup", value: x })}
          />

          <SelectDragBox
            title="Asset"
            color="red"
            add={() => addMaster("assets")}
            items={masters.assets.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No asset added"
            getData={(x) => ({ type: "asset", value: x })}
          />

          <SelectDragBox
            title="Liability"
            color="red"
            add={() => addMaster("liabilities")}
            items={masters.liabilities.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No liability added"
            getData={(x) => ({ type: "liability", value: x })}
          />
        </aside>
      </div>

      {popupOpen && (
        <div className="finance-overlay">
          <div className="finance-popup finance-entry-modal">
            <div className="finance-modal-header">
              <h3>
                {editingEntryId
                  ? entryType === "income"
                    ? "Edit Incoming Entry"
                    : "Edit Outgoing Entry"
                  : entryType === "income"
                    ? "Add Incoming Entry"
                    : "Add Outgoing Entry"}
              </h3>

              <div className="finance-modal-company">
                <span>Company</span>
                <select
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  disabled
                >
                  <option value="SVB">SVB</option>
                </select>
              </div>
            </div>

            <div className="finance-entry-basic-row">
              <label>
                Date
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                />
              </label>

              <label>
                Amount
                <input
                  type="number"
                  name="amount"
                  placeholder="Amount"
                  value={form.amount}
                  onChange={handleChange}
                />
              </label>
            </div>

            <div
              className={`finance-payment-row ${
                form.paymentMode === "Cash" ? "cash-mode" : ""
              }`}
            >
              <label>
                Payment Mode
                <select
                  name="paymentMode"
                  value={form.paymentMode}
                  onChange={handleChange}
                >
                  {PAYMENT_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
              </label>

              {form.paymentMode !== "Cash" && (
                <>
                  <label>
                    Bank
                    <select
                      name="bankAccount"
                      value={form.bankAccount}
                      onChange={handleChange}
                    >
                      {(COMPANY_BANKS[form.company] || []).map((bank) => (
                        <option key={bank} value={bank}>
                          {bank}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    GST %
                    <select
                      name="gstPercent"
                      value={form.gstPercent}
                      onChange={handleChange}
                    >
                      <option value="">No GST</option>
                      <option value="5">5%</option>
                      <option value="12">12%</option>
                      <option value="18">18%</option>
                    </select>
                  </label>

                  <label>
                    TDS %
                    <input
                      type="number"
                      name="tdsPercent"
                      placeholder="TDS %"
                      value={form.tdsPercent}
                      onChange={handleChange}
                    />
                  </label>
                </>
              )}
            </div>

            <div className="finance-document-row">
              <OtherDocumentUpload
                file={form.otherDocFile}
                onFileChange={(file) => handleFileChange("otherDocFile", file)}
              />

              <DocumentReferenceField
                title="Quotation"
                shortTitle="Quotation"
                inputId="finance-quote-file"
                documentValue={form.quoteDocumentId}
                documents={getDocumentsByType("quotation")}
                existingFiles={getExistingAttachedFiles("quotation")}
                attachedFileName={
                  form.quoteFile?.name || form.quoteExistingFileName
                }
                loading={documentsLoading}
                onDocumentChange={(value) =>
                  handleDocumentSelect("quotation", value)
                }
                file={form.quoteFile}
                onFileChange={(file) => handleFileChange("quoteFile", file)}
              />

              <DocumentReferenceField
                title="Proforma Invoice (PI)"
                shortTitle="PI"
                inputId="finance-proforma-file"
                documentValue={form.proformaDocumentId}
                documents={getDocumentsByType("proforma")}
                existingFiles={getExistingAttachedFiles("proforma")}
                attachedFileName={
                  form.proformaFile?.name || form.proformaExistingFileName
                }
                loading={documentsLoading}
                onDocumentChange={(value) =>
                  handleDocumentSelect("proforma", value)
                }
                file={form.proformaFile}
                onFileChange={(file) => handleFileChange("proformaFile", file)}
              />

              <DocumentReferenceField
                title="Bill / Invoice"
                shortTitle="Invoice"
                inputId="finance-invoice-file"
                documentValue={form.invoiceDocumentId}
                documents={getDocumentsByType("invoice")}
                existingFiles={getExistingAttachedFiles("invoice")}
                attachedFileName={
                  form.invoiceFile?.name || form.invoiceExistingFileName
                }
                loading={documentsLoading}
                onDocumentChange={(value) =>
                  handleDocumentSelect("invoice", value)
                }
                file={form.invoiceFile}
                onFileChange={(file) => handleFileChange("invoiceFile", file)}
              />
            </div>

            <label className="finance-narration-field">
              Note / Narration
              <textarea
                name="note"
                placeholder="Add note / narration"
                value={form.note}
                maxLength={500}
                onChange={handleChange}
                rows="3"
              />
              <small>{String(form.note || "").length} / 500</small>
            </label>

            <div className="amount-preview-card finance-modal-preview">
              <div>
                <span>Base Amount</span>
                <b>₹ {previewAmount.amount.toLocaleString("en-IN")}</b>
              </div>
              <div>
                <span>GST</span>
                <b>₹ {previewAmount.gstAmount.toLocaleString("en-IN")}</b>
              </div>
              <div>
                <span>TDS</span>
                <b>₹ {previewAmount.tdsAmount.toLocaleString("en-IN")}</b>
              </div>
              <div className="preview-total">
                <span>Total</span>
                <b>₹ {previewAmount.totalAmount.toLocaleString("en-IN")}</b>
              </div>
            </div>

            <div className="finance-popup-footer">
              <button className="cancel-btn" onClick={closePopup}>
                Cancel
              </button>

              <button
                className={`finance-btn ${
                  entryType === "income" ? "income-btn" : "expense-btn"
                }`}
                onClick={saveEntry}
                disabled={loading}
              >
                {loading
                  ? "Saving..."
                  : editingEntryId
                    ? "Update Entry"
                    : "Save for Review"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PaperclipIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className="finance-paperclip-svg"
    >
      <path
        d="M9.5 12.5 15.7 6.3a3 3 0 0 1 4.2 4.2l-8.1 8.1a5 5 0 1 1-7.1-7.1l8.5-8.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className="finance-upload-svg"
    >
      <path
        d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 15.5v2.25A2.25 2.25 0 0 0 7.25 20h9.5A2.25 2.25 0 0 0 19 17.75V15.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OtherDocumentUpload({ file, existingFileName = "", onFileChange }) {
  const inputId = "finance-other-document-file";

  return (
    <div className="finance-compact-doc">
      <div className="finance-compact-doc-title">Any Other Document</div>

      <input
        id={inputId}
        className="finance-hidden-file-input"
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
        onChange={(e) => onFileChange(e.target.files?.[0] || null)}
      />

      <label
        htmlFor={inputId}
        className={`finance-compact-upload-control ${
          file || existingFileName ? "has-file" : ""
        }`}
        title={file?.name || existingFileName || "Attach any other document"}
      >
        <PaperclipIcon />
        <span>{file?.name || existingFileName || "Attach Document"}</span>
      </label>
    </div>
  );
}

function DocumentReferenceField({
  title,
  shortTitle,
  inputId,
  documentValue,
  documents = [],
  existingFiles = [],
  attachedFileName = "",
  loading,
  onDocumentChange,
  file,
  onFileChange,
}) {
  const selectedValue = attachedFileName
    ? `file:${attachedFileName}`
    : documentValue || "";

  return (
    <div className="finance-compact-doc">
      <div className="finance-compact-doc-title">{title}</div>

      <div className="finance-compact-doc-control">
        <div className="finance-compact-select-wrap">
          <span className="finance-compact-leading-icon" aria-hidden="true">
            {attachedFileName ? "📎" : "▱"}
          </span>

          <select
            value={selectedValue}
            onChange={(e) => onDocumentChange(e.target.value)}
            disabled={loading}
            title={`Select existing ${title}`}
          >
            <option value="">
              {loading
                ? "Loading..."
                : `Select Existing ${shortTitle || title}`}
            </option>

            {documents.length > 0 && (
              <optgroup label="ERP Documents">
                {documents.map((doc) => (
                  <option key={doc._id} value={doc._id}>
                    {doc.number ||
                      doc.quotationNumber ||
                      doc.invoiceNumber ||
                      doc._id}
                    {doc.projectName ? ` — ${doc.projectName}` : ""}
                    {doc.client?.companyName
                      ? ` — ${doc.client.companyName}`
                      : ""}
                  </option>
                ))}
              </optgroup>
            )}

            {existingFiles.length > 0 && (
              <optgroup label="Previously Attached">
                {existingFiles.map((name) => (
                  <option key={`file:${name}`} value={`file:${name}`}>
                    📎 {name}
                  </option>
                ))}
              </optgroup>
            )}

            {attachedFileName && !existingFiles.includes(attachedFileName) && (
              <optgroup label="Current Attachment">
                <option value={`file:${attachedFileName}`}>
                  📎 {attachedFileName}
                </option>
              </optgroup>
            )}
          </select>
        </div>

        <input
          id={inputId}
          className="finance-hidden-file-input"
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
          onChange={(e) => onFileChange(e.target.files?.[0] || null)}
        />

        <label
          htmlFor={inputId}
          className={`finance-compact-attach-btn ${file ? "has-file" : ""}`}
          title={file ? `Attached: ${file.name}` : `Attach ${title}`}
          aria-label={file ? `Change attached ${title}` : `Attach ${title}`}
        >
          <PaperclipIcon />
        </label>
      </div>
    </div>
  );
}

function SelectDragBox({ title, color, items = [], emptyText, add, getData }) {
  const [selected, setSelected] = useState(null);
  const isRed = color === "red";
  const safeItems = Array.isArray(items) ? items : [];

  const options = useMemo(
    () =>
      safeItems.map((item) => ({
        value: item.value,
        label: item.label,
        raw: item.raw,
      })),
    [safeItems],
  );

  const handleDragStart = (e) => {
    if (!selected) {
      e.preventDefault();
      alert("Please select item first");
      return;
    }

    const dragData = getData(selected.raw);

    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("financeData", JSON.stringify(dragData));
    e.dataTransfer.setData("text/plain", selected.label);
  };

  return (
    <div className={`finance-drag-box ${isRed ? "box-red" : "box-green"}`}>
      <div className="drag-box-header">
        <h4>{title}</h4>

        {add && (
          <button className="add-master-btn" onClick={add}>
            + Add
          </button>
        )}
      </div>

      {safeItems.length === 0 ? (
        <EmptyText text={emptyText} />
      ) : (
        <>
          <Select
            className="finance-react-select"
            classNamePrefix="finance-select"
            value={selected}
            onChange={setSelected}
            options={options}
            placeholder="Select"
            isSearchable
            isClearable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            noOptionsMessage={() => "No result found"}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 99999 }),
            }}
          />

          <div
            draggable={!!selected}
            onDragStart={handleDragStart}
            className={`selected-drag-chip ${selected ? "active" : "disabled"}`}
          >
            <span className="drag-icon">⋮⋮</span>
            Drag Selected{selected ? `: ${selected.label}` : ""}
          </div>
        </>
      )}
    </div>
  );
}

function EmptyText({ text }) {
  return <div className="empty-text">{text}</div>;
}

function EntryCard({
  entry,
  type,
  canLock,
  onLock,
  onEdit,
  onDropToCard,
  onOpenSalesDocument,
  onOpenPaymentProof,
}) {
  const [showDetails, setShowDetails] = useState(false);
  const isIncome = type === "income";

  const baseAmount = Number(entry.amount || 0);
  const gstPercent = Number(entry.gstPercent || 0);
  const tdsPercent = Number(entry.tdsPercent || 0);

  const gstAmount =
    Number(entry.gstAmount || 0) > 0
      ? Number(entry.gstAmount || 0)
      : (baseAmount * gstPercent) / 100;

  const tdsAmount =
    Number(entry.tdsAmount || 0) > 0
      ? Number(entry.tdsAmount || 0)
      : (baseAmount * tdsPercent) / 100;

  const finalTotal =
    Number(entry.totalAmount || 0) > 0
      ? Number(entry.totalAmount || 0)
      : baseAmount + gstAmount - tdsAmount;

  const hasTax =
    gstPercent > 0 || tdsPercent > 0 || gstAmount > 0 || tdsAmount > 0;

  const missingOfficeUnit = !entry.officeUnit;
  const missingHead = !entry.head;
  const missingSubHead = !entry.subHead;
  const missingGroup = !entry.groupName;
  const missingProject = !entry.projectName;

  const attachments = [
    entry.quoteFileName && {
      label: "Quotation",
      name: entry.quoteFileName,
      key: entry.quoteFileKey || "",
    },
    entry.proformaFileName && {
      label: "PI",
      name: entry.proformaFileName,
      key: entry.proformaFileKey || "",
    },
    entry.invoiceFileName && {
      label: "Invoice",
      name: entry.invoiceFileName,
      key: entry.invoiceFileKey || "",
    },
    entry.otherDocFileName && {
      label: "Other",
      name: entry.otherDocFileName,
      key: entry.otherDocFileKey || "",
    },
  ].filter(Boolean);

  const erpDocuments = [
    entry.quoteNo && {
      label: "Quotation",
      name: entry.quoteNo,
      documentId: entry.quoteDocumentId?._id || entry.quoteDocumentId || "",
    },
    entry.proformaNo && {
      label: "PI",
      name: entry.proformaNo,
      documentId:
        entry.proformaDocumentId?._id || entry.proformaDocumentId || "",
    },
    entry.invoiceNo && {
      label: "Invoice",
      name: entry.invoiceNo,
      documentId: entry.invoiceDocumentId?._id || entry.invoiceDocumentId || "",
    },
  ].filter(Boolean);

  const openAttachment = async (doc) => {
    try {
      if (!doc?.key) return;

      const response = await axios.get(
        `${API_BASE_URL}/api/download?key=${encodeURIComponent(doc.key)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob",
        },
      );

      const fileURL = URL.createObjectURL(response.data);
      const newWindow = window.open(fileURL, "_blank", "noopener,noreferrer");

      if (!newWindow) {
        alert("Please allow pop-ups to open the document.");
      }

      setTimeout(() => {
        URL.revokeObjectURL(fileURL);
      }, 60000);
    } catch (err) {
      console.error("Document open error:", err);

      let message = "Document open nahi ho raha.";

      if (err.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const parsed = JSON.parse(errorText);
          message = parsed?.message || parsed?.error || message;
        } catch (_) {
          // Keep fallback message
        }
      } else {
        message =
          err.response?.data?.message || err.response?.data?.error || message;
      }

      alert(message);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const raw = e.dataTransfer.getData("financeData");
    if (!raw) return;

    const data = JSON.parse(raw);
    onDropToCard(entry, data);
  };

  return (
    <div
      className={`entry-card ${isIncome ? "entry-income" : "entry-expense"}`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <div className="entry-card-inner">
        <div className="entry-card-header">
          <div>
            <div className="entry-type">
              {isIncome ? "Incoming Entry" : "Outgoing Entry"}
            </div>

            <h2 className="entry-amount">
              ₹ {finalTotal.toLocaleString("en-IN")}
            </h2>
          </div>

          <div className="entry-header-actions">
            {!entry.locked && (
              <button
                type="button"
                className="entry-edit-btn"
                onClick={() => onEdit(entry)}
              >
                ✎ Edit
              </button>
            )}

            <span className={entry.locked ? "locked-badge" : "pending-badge"}>
              {entry.locked ? "🔒 Locked" : "Pending Review"}
            </span>
          </div>
        </div>

        {/* Compact card: only Date + Company */}
        <div className="entry-info-grid entry-summary-grid">
          <InfoItem
            label="Date"
            value={
              entry.date
                ? new Date(entry.date).toLocaleDateString("en-IN")
                : "-"
            }
          />

          <InfoItem
            label="Company"
            value={entry.company || "Nil"}
            isMissing={!entry.company}
          />

          <InfoItem
            label="Bank"
            value={
              entry.paymentMode === "Cash" ? "Cash" : entry.bankAccount || "Nil"
            }
            isMissing={entry.paymentMode !== "Cash" && !entry.bankAccount}
          />

          <InfoItem
            label="Payment Mode"
            value={entry.paymentMode || "Nil"}
            isMissing={!entry.paymentMode}
          />
        </div>

        <button
          type="button"
          className={`entry-view-more-btn ${showDetails ? "open" : ""}`}
          onClick={() => setShowDetails((prev) => !prev)}
          aria-expanded={showDetails}
        >
          <span>{showDetails ? "View Less" : "View More"}</span>
          <span className="entry-view-more-arrow">
            {showDetails ? "▲" : "▼"}
          </span>
        </button>

        {showDetails && (
          <div className="entry-expanded-details">
            {hasTax && (
              <div className="amount-breakup entry-expanded-amount-breakup">
                <b>Amount:</b> ₹ {baseAmount.toLocaleString("en-IN")}
                {gstAmount > 0 &&
                  ` + ₹ ${gstAmount.toLocaleString("en-IN")} GST (${gstPercent}%)`}
                {tdsAmount > 0 &&
                  ` - ₹ ${tdsAmount.toLocaleString("en-IN")} TDS (${tdsPercent}%)`}
                {` = ₹ ${finalTotal.toLocaleString("en-IN")}`}
              </div>
            )}

            {/* All mapping / accounting details */}
            <div className="entry-info-grid entry-detail-grid">
              <InfoItem
                label="Office Unit"
                value={entry.officeUnit || "Nil"}
                isMissing={missingOfficeUnit}
              />

              <InfoItem
                label={isIncome ? "Income Parent Head" : "Expense Parent Head"}
                value={entry.head || "Nil"}
                isMissing={missingHead}
              />

              <InfoItem
                label={isIncome ? "Income Sub Head" : "Expense Sub Head"}
                value={entry.subHead || "Nil"}
                isMissing={missingSubHead}
              />

              <InfoItem
                label="Group"
                value={entry.groupName || "Nil"}
                isMissing={missingGroup}
              />

              <InfoItem
                label="Project"
                value={entry.projectName || "Nil"}
                isMissing={missingProject}
              />

              <InfoItem
                label={isIncome ? "Customer" : "Vendor"}
                value={entry.partyName || "Nil"}
                isMissing={!entry.partyName}
              />

              <InfoItem
                label="Asset"
                value={
                  entry.classificationType === "asset"
                    ? entry.classification || "Nil"
                    : "Nil"
                }
                isMissing={
                  entry.classificationType !== "asset" || !entry.classification
                }
              />

              <InfoItem
                label="Liability"
                value={
                  entry.classificationType === "liability"
                    ? entry.classification || "Nil"
                    : "Nil"
                }
                isMissing={
                  entry.classificationType !== "liability" ||
                  !entry.classification
                }
              />

              {entry.billNo && (
                <InfoItem label="Bill No." value={entry.billNo} />
              )}

              {gstPercent > 0 && (
                <InfoItem
                  label="GST"
                  value={`${gstPercent}% | ₹ ${gstAmount.toLocaleString("en-IN")}`}
                />
              )}

              {tdsPercent > 0 && (
                <InfoItem
                  label="TDS"
                  value={`${tdsPercent}% | ₹ ${tdsAmount.toLocaleString("en-IN")}`}
                />
              )}
            </div>

            {entry.note && (
              <div className="entry-note-box">
                <span>Narration / Note</span>
                <b>{entry.note}</b>
              </div>
            )}

            {(attachments.length > 0 ||
              erpDocuments.length > 0 ||
              entry.paymentProofUrl) && (
              <div className="entry-documents-section">
                <div className="entry-documents-title">Documents</div>

                <div className="entry-documents-list">
                  {erpDocuments.map((doc) =>
                    doc.documentId ? (
                      <button
                        type="button"
                        key={`erp-${doc.label}-${doc.name}`}
                        className="entry-document-chip erp-document-chip entry-document-link"
                        title={`Open ${doc.label}: ${doc.name}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onOpenSalesDocument?.(doc.documentId);
                        }}
                      >
                        <span className="entry-document-icon">▱</span>
                        <span>
                          <small>{doc.label}</small>
                          <b>{doc.name}</b>
                        </span>
                        <span className="entry-document-open-icon">↗</span>
                      </button>
                    ) : (
                      <div
                        key={`erp-${doc.label}-${doc.name}`}
                        className="entry-document-chip erp-document-chip entry-document-unavailable"
                        title={`${doc.label}: ${doc.name} — document ID not available`}
                      >
                        <span className="entry-document-icon">▱</span>
                        <span>
                          <small>{doc.label}</small>
                          <b>{doc.name}</b>
                        </span>
                      </div>
                    ),
                  )}

                  {entry.paymentProofUrl && (
                    <button
                      type="button"
                      className="entry-document-chip entry-document-link payment-proof-chip"
                      title={`Open Payment Proof${
                        entry.paymentProofName
                          ? `: ${entry.paymentProofName}`
                          : ""
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpenPaymentProof?.(entry.paymentProofUrl);
                      }}
                    >
                      <span className="entry-document-icon">📎</span>
                      <span>
                        <small>Payment Proof</small>
                        <b>{entry.paymentProofName || "Open Proof"}</b>
                      </span>
                      <span className="entry-document-open-icon">↗</span>
                    </button>
                  )}

                  {attachments.map((doc) =>
                    doc.key ? (
                      <button
                        type="button"
                        key={`file-${doc.label}-${doc.name}`}
                        className="entry-document-chip entry-document-link"
                        title={`Open ${doc.label}: ${doc.name}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openAttachment(doc);
                        }}
                      >
                        <span className="entry-document-icon">📎</span>
                        <span>
                          <small>{doc.label}</small>
                          <b>{doc.name}</b>
                        </span>
                        <span className="entry-document-open-icon">↗</span>
                      </button>
                    ) : (
                      <div
                        key={`file-${doc.label}-${doc.name}`}
                        className="entry-document-chip entry-document-unavailable"
                        title={`${doc.label}: ${doc.name} — file path not available`}
                      >
                        <span className="entry-document-icon">📎</span>
                        <span>
                          <small>{doc.label}</small>
                          <b>{doc.name}</b>
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

            {!entry.locked && canLock && (
              <button
                type="button"
                className="lock-btn"
                onClick={() => onLock(entry._id)}
              >
                🔒 Lock Entry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoItem({ label, value, isMissing }) {
  return (
    <div className={`entry-info-item ${isMissing ? "missing-info-item" : ""}`}>
      <span>{label}</span>
      <b>{isMissing ? `!! ${value}` : value}</b>
    </div>
  );
}
